// Người đang dùng: bootstrap (cổng vào, quyền hiệu lực, module hiển thị, cấu hình công ty).
// Frontend KHÔNG lọc dữ liệu theo quyền — chỉ dùng can() để ẩn/hiện thao tác.
import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { atLeast, can, scopeOf, type Actor, type Owned, type Scope, type identity } from '@cathouse/contracts';
import { accessChanged, authzChanged, http, qk, refreshSession } from '@/shared/api';

export type Bootstrap = identity.Bootstrap;

export const bootstrapQuery = () => ({
  queryKey: qk.viewer.bootstrap(),
  queryFn: () => http.get<Bootstrap>('/me/bootstrap'),
  staleTime: 60_000,
});

export const useBootstrap = () => useQuery(bootstrapQuery());

/** Bootstrap đã nạp (dùng bên trong khung ứng dụng — route cha đã đảm bảo có dữ liệu). */
export function useViewer(): Bootstrap {
  const { data } = useBootstrap();
  if (!data) throw new Error('Viewer chưa sẵn sàng');
  return data;
}

export const actorOf = (b: Bootstrap): Actor => ({
  userId: b.user.id,
  staffId: b.account?.staffId ?? null,
  roleId: b.role?.id ?? null,
  status: b.account?.status ?? 'none',
  pv: b.pv,
  grants: b.grants,
  team: b.team,
});

export function useCan() {
  const b = useViewer();
  const actor = actorOf(b);
  return {
    can: (perm: string, rec?: Owned) => can(actor, perm, rec),
    any: (...perms: string[]) => perms.some((p) => can(actor, p)),
    scope: (perm: string): Scope | null => scopeOf(actor, perm),
    atLeast: (perm: string, s: Scope) => atLeast(actor, perm, s),
    staffId: actor.staffId,
  };
}

/** Quyền đổi ở máy chủ (X-Authz-Version > pv hiện có) ⇒ làm mới token + bootstrap. */
export function useAuthzSync() {
  const qc = useQueryClient();
  useEffect(
    () =>
      authzChanged.on((pv) => {
        const cur = qc.getQueryData<Bootstrap>(qk.viewer.bootstrap());
        if (cur && pv <= cur.pv) return;
        void refreshSession().then(() => invalidateViewer(qc));
      }),
    [qc],
  );
  useEffect(() => accessChanged.on(() => void invalidateViewer(qc)), [qc]);
}

export const invalidateViewer = (qc: QueryClient) => qc.invalidateQueries({ queryKey: qk.viewer.all });
